import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type {
    Blocks,
    ContentBlock,
    FaqItem,
    Settings,
    SiteFrame
} from '@/types/content'

import { Container } from '@/components/generals/container'
import { CtaButton } from '@/components/generals/cta-button'
import { Layout } from '@/components/generals/layout'
import { SectionIntro } from '@/components/generals/section-intro'
import { Seo } from '@/components/generals/seo'
import { FaqList } from '@/components/sections/faq-list'
import { PageHero } from '@/components/sections/page-hero'
import { cms, hasValue, slugify } from '@/lib/cms'
import { reveal } from '@/lib/reveal'
import { REVALIDATE_SECONDS, getFaqs, getPageContent } from '@/lib/content'

interface FaqPageProps {
    frame: SiteFrame
    blocks: Blocks
    faqs: Array<FaqItem>
}

interface FaqGroup {
    category: string
    anchor: string
    // Row whose "kategorie" value is shown as the group heading
    source: FaqItem
    items: Array<FaqItem>
}

const groupByCategory = (faqs: Array<FaqItem>): Array<FaqGroup> => {
    const groups: Array<FaqGroup> = []

    for (const item of faqs) {
        const existing = groups.find(group => group.category === item.kategorie)

        if (existing) existing.items.push(item)
        else groups.push({ category: item.kategorie, anchor: `fragen-${slugify(item.kategorie)}`, source: item, items: [item] })
    }

    return groups
}

interface AskSectionProps {
    block?: ContentBlock
    settings: Settings
}

const AskSection: FC<AskSectionProps> = ({ block, settings }): ReactNode => {
    if (!block) return null

    return (
        <section aria-labelledby='frage-titel' className='relative bg-stone py-20 sm:py-28'>
            <Container className='flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between'>
                <SectionIntro block={block} headingId='frage-titel' size='md' className='max-w-2xl' />

                <div {...reveal(150)}>
                    <CtaButton settings={settings} size='lg' />
                </div>
            </Container>
        </section>
    )
}

const FaqPage: FC<FaqPageProps> = ({ frame, blocks, faqs }): ReactNode => {
    const seo = blocks['faq.seo']
    const groups = groupByCategory(faqs)

    // Only confirmed answers are handed to search engines as structured data.
    const confirmed = faqs.filter(item => item.bestaetigt && hasValue(item.antwort))
    const jsonLd = confirmed.length > 0
        ? [{
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: confirmed.map(item => ({
                '@type': 'Question',
                name: item.frage,
                acceptedAnswer: { '@type': 'Answer', text: item.antwort }
            }))
        }]
        : []

    return (
        <Layout frame={frame}>
            <Seo
                title={seo?.ueberschrift ?? 'FAQ'}
                description={seo?.text ?? undefined}
                siteUrl={frame.settings.webseite}
                path='/faq'
                jsonLd={jsonLd}
            />

            <PageHero block={blocks['faq.hero']} />

            <section aria-label='Fragen und Antworten' className='relative bg-paper py-20 sm:py-28 lg:py-32'>
                <Container className='grid gap-12 lg:grid-cols-12 lg:gap-10'>
                    {groups.length > 1 && (
                        <nav aria-label='Themen' className='lg:col-span-3'>
                            <ul className='flex flex-wrap gap-2 lg:sticky lg:top-[calc(var(--header-height)+2rem)] lg:flex-col lg:gap-1'>
                                {groups.map(group => (
                                    <li key={group.anchor}>
                                        <a
                                            href={`#${group.anchor}`}
                                            className='inline-flex rounded-full border border-ink/15 px-4 py-2 text-sm font-medium transition-colors duration-300 hover:border-ink hover:bg-ink hover:text-bone lg:border-transparent lg:px-0 lg:hover:border-transparent lg:hover:bg-transparent lg:hover:text-ember'
                                        >
                                            <span data-mdlfy-cms={cms('faq', group.source.id, 'kategorie')}>{group.category}</span>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    )}

                    <div className={groups.length > 1 ? 'flex flex-col gap-20 lg:col-span-8 lg:col-start-5' : 'flex flex-col gap-20 lg:col-span-10'}>
                        {groups.map(group => (
                            <div key={group.anchor} id={group.anchor} className='flex scroll-mt-[calc(var(--header-height)+2rem)] flex-col gap-8' {...reveal()}>
                                <h2 className='type-eyebrow flex items-center gap-3 text-ember'>
                                    <span aria-hidden='true' className='h-px w-8 bg-current' />
                                    <span data-mdlfy-cms={cms('faq', group.source.id, 'kategorie')}>{group.category}</span>
                                </h2>

                                <FaqList items={group.items} />
                            </div>
                        ))}

                        {groups.length === 0 && (
                            <p className='is-placeholder self-start text-smoke'>
                                Noch keine Fragen veröffentlicht. Fragen im CMS unter FAQ anlegen.
                            </p>
                        )}
                    </div>
                </Container>
            </section>

            <AskSection block={blocks['faq.kontakt']} settings={frame.settings} />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<FaqPageProps> = async () => {
    const [content, faqs] = await Promise.all([
        getPageContent({ blocks: ['faq.seo', 'faq.hero', 'faq.kontakt'] }),
        getFaqs()
    ])

    return {
        props: {
            frame: content.frame,
            blocks: content.blocks,
            faqs
        },
        revalidate: REVALIDATE_SECONDS
    }
}

export default FaqPage
